import { useEffect, useMemo, useState } from "react";

import { useModalScrollLock } from "../hooks/use-modal-scroll-lock";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { appCopy } from "../lib/i18n/app";
import { ScreenShell } from "./screen-shell";
import { Eyebrow, H1, MonoMeta, TopBar } from "./ui/primitives";

type UserRow = Awaited<ReturnType<typeof api.adminListUsers>>["users"][number];
type Overview = Awaited<ReturnType<typeof api.adminOverview>>;
type UserDetail = Awaited<ReturnType<typeof api.adminUserDetail>>;

type Props = {
  onBack: () => void;
};

function centsToUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDate(ts: number | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")} ${String(
    d.getHours(),
  ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

type SortBy = "cost" | "calls" | "created" | "last" | "invites";

export function AdminScreen({ onBack }: Props) {
  const { lang } = useLang();
  const t = appCopy[lang];
  const [overview, setOverview] = useState<Overview | null>(null);
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>("cost");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.adminOverview(), api.adminListUsers()])
      .then(([o, u]) => {
        if (cancelled) return;
        setOverview(o);
        setUsers(u.users);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t.adminNoAccess);
      });
    return () => {
      cancelled = true;
    };
  }, [t.adminNoAccess]);

  const sorted = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    const filtered = q
      ? users.filter(
          (u) => u.email.toLowerCase().includes(q) || (u.name ?? "").toLowerCase().includes(q),
        )
      : users;
    return [...filtered].sort((a, b) => {
      if (sortBy === "cost") return b.totalCostCents - a.totalCostCents;
      if (sortBy === "calls") return b.totalAiCalls - a.totalAiCalls;
      if (sortBy === "created") return b.createdAt - a.createdAt;
      if (sortBy === "invites") return b.inviteesCount - a.inviteesCount;
      return (b.lastCallAt ?? 0) - (a.lastCallAt ?? 0);
    });
  }, [users, sortBy, search]);

  async function openUser(id: string) {
    try {
      const detail = await api.adminUserDetail(id);
      setSelectedUser(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.adminLoadFailed);
    }
  }

  return (
    <ScreenShell hideHeader>
      <TopBar back={t.adminBack} title="ADMIN" onBack={onBack} />

      <div style={{ paddingTop: 18, paddingBottom: 8 }}>
        <Eyebrow>{t.adminEyebrow}</Eyebrow>
        <H1 accent="." className="mt-3">
          {t.adminHeading}
        </H1>
      </div>

      {error ? (
        <p
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
        >
          {error}
        </p>
      ) : null}

      {/* Overview cards */}
      {overview ? (
        <section style={{ marginTop: 16 }}>
          <div className="grid grid-cols-2 gap-2">
            <KpiCard
              label={t.adminKpiUsers}
              value={overview.totalUsers}
              sub={t.adminKpiUsersSub(overview.verifiedUsers)}
            />
            <KpiCard
              label={t.adminKpiActive7d}
              value={overview.activeLast7d}
              sub={`${overview.totalUsers > 0 ? Math.round((overview.activeLast7d / overview.totalUsers) * 100) : 0}%`}
            />
            <KpiCard
              label={t.adminKpiCostMonth}
              value={centsToUsd(overview.thisMonthCostCents)}
              sub={t.adminKpiCostMonthSub(overview.totalAiCalls)}
              accent
            />
            <KpiCard
              label={t.adminKpiCostTotal}
              value={centsToUsd(overview.totalCostCentsAllTime)}
              sub={t.adminKpiCostTotalSub(overview.costAlertsLast30d)}
            />
            <KpiCard
              label={t.adminKpiReferrals}
              value={overview.totalReferrals}
              sub={t.adminKpiReferralsSub(overview.topReferrers.length)}
              accent
            />
          </div>

          {/* Cost by model */}
          <div style={{ marginTop: 22 }}>
            <Eyebrow>{t.adminCostByModel}</Eyebrow>
            <div style={{ marginTop: 8 }}>
              {overview.costByModel.length === 0 ? (
                <p className="text-[13px]" style={{ color: "var(--muted)" }}>
                  {t.adminNoData}
                </p>
              ) : null}
              {overview.costByModel.map((m) => (
                <div
                  key={m.model}
                  className="py-2.5 flex items-center justify-between"
                  style={{ borderTop: "1px solid var(--nav-border)" }}
                >
                  <div>
                    <div className="text-[13px] font-semibold" style={{ color: "var(--ink)" }}>
                      {m.model}
                    </div>
                    <MonoMeta>
                      {m.calls} {t.adminCalls}
                    </MonoMeta>
                  </div>
                  <div
                    className="tabular"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 16,
                      fontWeight: 700,
                      color: "var(--ink)",
                    }}
                  >
                    {centsToUsd(m.costCents)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top referrers */}
          {overview.topReferrers.length > 0 ? (
            <div style={{ marginTop: 22 }}>
              <Eyebrow>{t.adminTopReferrers}</Eyebrow>
              <div style={{ marginTop: 8 }}>
                {overview.topReferrers.map((r) => (
                  <button
                    key={r.userId}
                    type="button"
                    onClick={() => openUser(r.userId)}
                    className="w-full text-left py-2.5 flex items-center justify-between gap-3"
                    style={{ borderTop: "1px solid var(--nav-border)" }}
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-[13px] truncate"
                        style={{ color: "var(--ink)" }}
                      >
                        {r.email}
                      </div>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          color: "var(--muted)",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {r.inviteCode}
                      </span>
                    </div>
                    <span
                      className="tabular shrink-0"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: 18,
                        color: "var(--accent)",
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {r.inviteesCount}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Top spenders */}
          {overview.topSpendersThisMonth.length > 0 ? (
            <div style={{ marginTop: 22 }}>
              <Eyebrow>{t.adminTopSpenders}</Eyebrow>
              <div style={{ marginTop: 8 }}>
                {overview.topSpendersThisMonth.map((s) => (
                  <button
                    key={s.userId}
                    type="button"
                    onClick={() => openUser(s.userId)}
                    className="w-full text-left py-2.5 flex items-center justify-between"
                    style={{ borderTop: "1px solid var(--nav-border)" }}
                  >
                    <span className="text-[13px] truncate" style={{ color: "var(--ink)" }}>
                      {s.email}
                    </span>
                    <span
                      className="tabular"
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 12,
                        color: "var(--accent)",
                        fontWeight: 700,
                      }}
                    >
                      {centsToUsd(s.costCents)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ) : (
        <p className="text-[13px]" style={{ marginTop: 16, color: "var(--muted)" }}>
          {t.adminLoading}
        </p>
      )}

      {/* User table */}
      {users ? (
        <section style={{ marginTop: 26 }}>
          <Eyebrow>{t.adminAllUsers(users.length)}</Eyebrow>

          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.adminSearchPlaceholder}
            style={{
              marginTop: 10,
              width: "100%",
              height: 40,
              padding: "0 14px",
              background: "var(--surface)",
              color: "var(--ink)",
              borderRadius: 10,
              boxShadow: "inset 0 0 0 1px var(--nav-border)",
              // 16px iOS auto-zoom guard.
              fontSize: 16,
              outline: "none",
            }}
          />

          <div className="flex flex-wrap gap-2" style={{ marginTop: 10 }}>
            {(["cost", "calls", "invites", "created", "last"] as SortBy[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setSortBy(k)}
                className="text-[10px] uppercase tracking-[0.06em] font-bold px-3 py-1.5 rounded-md"
                style={{
                  fontFamily: "var(--font-mono)",
                  background: sortBy === k ? "var(--ink)" : "transparent",
                  color: sortBy === k ? "var(--bg)" : "var(--ink-2)",
                  boxShadow: sortBy === k ? "none" : "inset 0 0 0 1px var(--nav-border)",
                }}
              >
                {k === "cost"
                  ? t.adminSortCost
                  : k === "calls"
                    ? t.adminSortCalls
                    : k === "invites"
                      ? t.adminSortInvites
                      : k === "created"
                        ? t.adminSortCreated
                        : t.adminSortActive}
              </button>
            ))}
          </div>

          <div style={{ marginTop: 12 }}>
            {sorted.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => openUser(u.id)}
                className="w-full text-left py-3 flex items-center gap-3"
                style={{ borderTop: "1px solid var(--nav-border)" }}
              >
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[14px] font-semibold truncate"
                    style={{ color: "var(--ink)" }}
                  >
                    {u.name ?? u.email}
                    {u.emailVerifiedAt ? null : (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 9,
                          padding: "2px 6px",
                          borderRadius: 3,
                          background: "var(--warn-bg)",
                          color: "var(--warn-fg)",
                          fontFamily: "var(--font-mono)",
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                        }}
                      >
                        {t.adminUnverifiedBadge}
                      </span>
                    )}
                  </div>
                  <MonoMeta>
                    {u.email} · {u.totalAiCalls} AI · {u.mealCount} MEALS ·{" "}
                    {t.adminUserMetaLast} {fmtDate(u.lastCallAt)}
                  </MonoMeta>
                  <MonoMeta>
                    {t.adminUserMetaCode} {u.inviteCode ?? "—"} · {u.inviteesCount}{" "}
                    {t.adminUserMetaInvites}
                    {u.invitedByCode ? ` · ${t.adminUserMetaFrom} ${u.invitedByCode}` : ""}
                  </MonoMeta>
                </div>
                <div className="text-right shrink-0">
                  <div
                    className="tabular"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 18,
                      fontWeight: 700,
                      color: "var(--ink)",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {centsToUsd(u.totalCostCents)}
                  </div>
                  <MonoMeta>
                    {centsToUsd(u.thisMonthCostCents)}
                    {t.adminPerMonth}
                  </MonoMeta>
                </div>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {selectedUser ? (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      ) : null}
    </ScreenShell>
  );
}

function KpiCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 12,
        background: "var(--surface)",
        boxShadow: "0 0 0 1px var(--nav-border)",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          letterSpacing: "0.12em",
          color: "var(--muted)",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        className="tabular"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 28,
          fontWeight: 800,
          color: accent ? "var(--accent)" : "var(--ink)",
          letterSpacing: "-0.03em",
          lineHeight: 1.05,
          marginTop: 4,
        }}
      >
        {value}
      </div>
      {sub ? (
        <div style={{ marginTop: 4 }}>
          <MonoMeta>{sub}</MonoMeta>
        </div>
      ) : null}
    </div>
  );
}

function UserDetailModal({
  user,
  onClose,
}: {
  user: NonNullable<UserDetail>;
  onClose: () => void;
}) {
  const { lang } = useLang();
  const t = appCopy[lang];
  // <dialog open> is non-modal — body would scroll underneath this bottom
  // sheet on iOS WKWebView (gotchas handoff §4.1).
  useModalScrollLock(true);
  return (
    <dialog
      open
      aria-modal="true"
      className="fixed inset-0 z-50 m-0 flex h-full w-full items-end justify-center bg-transparent p-0"
    >
      <button
        type="button"
        aria-label={t.adminCloseAria}
        onClick={onClose}
        className="absolute inset-0"
        style={{
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      />
      <div
        className="relative w-full mx-auto"
        style={{
          maxWidth: 480,
          background: "var(--bg)",
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          padding: "20px 22px max(28px, env(safe-area-inset-bottom))",
          maxHeight: "85dvh",
          overflow: "auto",
          boxShadow: "0 -10px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div
          aria-hidden
          className="mx-auto mb-3"
          style={{ width: 40, height: 4, borderRadius: 99, background: "var(--track)" }}
        />
        <H1 size={42} accent=".">
          {user.name ?? user.email.split("@")[0]}
        </H1>
        <div style={{ marginTop: 8 }}>
          <MonoMeta>{user.email}</MonoMeta>
        </div>

        <div className="grid grid-cols-3 gap-2" style={{ marginTop: 18 }}>
          <KpiCard label={t.adminDetailCalls} value={user.totalAiCalls} />
          <KpiCard label={t.adminDetailTotal} value={centsToUsd(user.totalCostCents)} accent />
          <KpiCard label={t.adminDetailMonth} value={centsToUsd(user.thisMonthCostCents)} />
        </div>

        <div style={{ marginTop: 22 }}>
          <Eyebrow>{t.adminDetailReferral}</Eyebrow>
          <div
            className="py-2 flex items-center justify-between"
            style={{ borderTop: "1px solid var(--nav-border)", marginTop: 8 }}
          >
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold" style={{ color: "var(--ink)" }}>
                {t.adminDetailOwnCode}
              </div>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "var(--muted)",
                }}
              >
                {user.inviteCode ?? t.adminDetailNoCode}
              </span>
            </div>
            <span
              className="tabular"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 24,
                fontWeight: 700,
                color: "var(--accent)",
                letterSpacing: "-0.02em",
              }}
            >
              {user.inviteesCount}
            </span>
          </div>
          {user.invitedByCode ? (
            <div
              className="py-2 flex items-center justify-between"
              style={{ borderTop: "1px solid var(--nav-border)" }}
            >
              <div className="text-[13px]" style={{ color: "var(--ink-2)" }}>
                {t.adminDetailInvitedBy}
              </div>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "var(--ink)",
                  fontWeight: 700,
                }}
              >
                {user.invitedByCode}
              </span>
            </div>
          ) : null}
        </div>

        <div style={{ marginTop: 22 }}>
          <Eyebrow>{t.adminCostByModel}</Eyebrow>
          <div style={{ marginTop: 8 }}>
            {user.callsByModel.map((m) => (
              <div
                key={m.model}
                className="py-2 flex items-center justify-between"
                style={{ borderTop: "1px solid var(--nav-border)" }}
              >
                <div>
                  <div className="text-[13px] font-semibold" style={{ color: "var(--ink)" }}>
                    {m.model}
                  </div>
                  <MonoMeta>{m.calls} CALLS</MonoMeta>
                </div>
                <span
                  className="tabular"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                >
                  {centsToUsd(m.costCents)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 22 }}>
          <Eyebrow>{t.adminDetailRecentCalls}</Eyebrow>
          <div style={{ marginTop: 8 }}>
            {user.recentCalls.slice(0, 30).map((c, idx) => (
              <div
                key={`${c.at}-${idx}`}
                className="py-2 flex items-center justify-between"
                style={{ borderTop: "1px solid var(--nav-border)" }}
              >
                <div>
                  <div className="text-[13px]" style={{ color: "var(--ink)" }}>
                    {c.endpoint}
                  </div>
                  <MonoMeta>
                    {fmtDate(c.at)} · {c.model}
                  </MonoMeta>
                </div>
                <span
                  className="tabular"
                  style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-2)" }}
                >
                  {centsToUsd(c.costCents)}
                </span>
              </div>
            ))}
            {user.recentCalls.length === 0 ? (
              <p className="text-[13px]" style={{ color: "var(--muted)" }}>
                {t.adminDetailNoCalls}
              </p>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 22,
            width: "100%",
            padding: "14px 20px",
            borderRadius: 12,
            background: "transparent",
            color: "var(--ink)",
            boxShadow: "inset 0 0 0 1px var(--nav-border)",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {t.adminDetailClose}
        </button>
      </div>
    </dialog>
  );
}
